import { z } from "zod";
import { APPROVAL_STATUSES, ROLES } from "../enums.js";
import { SELF_REGISTER_ROLES } from "../users.js";
/**
 * Kebijakan kata sandi tunggal: dipakai saat pengguna mengganti kata sandinya
 * sendiri maupun saat administrator membuat akun baru.
 * Label dipakai agar pesan galat tetap spesifik pada konteksnya.
 */
export function strongPasswordSchema(label = "Kata sandi") {
    return z
        .string()
        .min(10, `${label} minimal 10 karakter.`)
        .max(200, `${label} maksimal 200 karakter.`)
        .regex(/[A-Z]/, `${label} harus memuat huruf kapital.`)
        .regex(/[a-z]/, `${label} harus memuat huruf kecil.`)
        .regex(/[0-9]/, `${label} harus memuat angka.`);
}
export const loginSchema = z.object({
    username: z.string().trim().min(1, "Username wajib diisi.").max(100),
    password: z.string().min(1, "Kata sandi wajib diisi.").max(200),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, "Kata sandi saat ini wajib diisi.").max(200),
    newPassword: strongPasswordSchema("Kata sandi baru"),
});
/**
 * Pembuatan akun oleh administrator (menu Kelola Pengguna).
 * Kolom fakultas/program studi/unit bersifat opsional di skema karena
 * kewajibannya bergantung pada peran; kelengkapan dan kesesuaiannya diperiksa
 * di lapisan lingkup (apps/api/src/auth/scope.ts).
 */
export const createUserSchema = z.object({
    username: z
        .string()
        .trim()
        .min(3, "Username minimal 3 karakter.")
        .max(60, "Username maksimal 60 karakter.")
        .regex(/^[a-z0-9][a-z0-9._-]*$/i, "Username hanya boleh memuat huruf, angka, titik, garis bawah, dan tanda hubung."),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email("Email tidak valid.")
        .max(150, "Email maksimal 150 karakter."),
    fullName: z
        .string()
        .trim()
        .min(3, "Nama lengkap minimal 3 karakter.")
        .max(150, "Nama lengkap maksimal 150 karakter."),
    identifierNumber: z
        .string()
        .trim()
        .min(1, "Nomor induk tidak boleh kosong.")
        .max(60, "Nomor induk maksimal 60 karakter.")
        .optional(),
    role: z.enum(ROLES),
    facultyId: z.string().trim().min(1).max(60).optional(),
    studyProgramId: z.string().trim().min(1).max(60).optional(),
    unitId: z.string().trim().min(1).max(60).optional(),
    password: strongPasswordSchema(),
});
/**
 * Pendaftaran mandiri (halaman /register). Peran dibatasi ke daftar
 * SELF_REGISTER_ROLES; lingkup wajib diperiksa di lapisan lingkup server
 * sehingga klien tidak dapat mendaftarkan diri sebagai admin.
 */
export const registerSchema = z.object({
    username: z
        .string()
        .trim()
        .min(3, "Username minimal 3 karakter.")
        .max(60, "Username maksimal 60 karakter.")
        .regex(/^[a-z0-9][a-z0-9._-]*$/i, "Username hanya boleh memuat huruf, angka, titik, garis bawah, dan tanda hubung."),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email("Email tidak valid.")
        .max(150, "Email maksimal 150 karakter."),
    fullName: z
        .string()
        .trim()
        .min(3, "Nama lengkap minimal 3 karakter.")
        .max(150, "Nama lengkap maksimal 150 karakter."),
    identifierNumber: z
        .string()
        .trim()
        .min(1, "Nomor induk tidak boleh kosong.")
        .max(60, "Nomor induk maksimal 60 karakter.")
        .optional(),
    role: z.enum(SELF_REGISTER_ROLES, "Peran tersebut tidak dapat didaftarkan sendiri."),
    studyProgramId: z.string().trim().min(1).max(60).optional(),
    unitId: z.string().trim().min(1).max(60).optional(),
    password: strongPasswordSchema(),
});
/** Alasan penolakan pendaftaran akun (wajib, minimal 5 karakter). */
export const approvalRejectionSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(5, "Alasan penolakan minimal 5 karakter.")
        .max(500, "Alasan penolakan maksimal 500 karakter."),
});
export const userSummarySchema = z.object({
    id: z.string(),
    username: z.string(),
    email: z.string(),
    fullName: z.string(),
    identifierNumber: z.string().nullable(),
    role: z.enum(ROLES),
    institutionId: z.string().nullable(),
    facultyId: z.string().nullable(),
    studyProgramId: z.string().nullable(),
    unitId: z.string().nullable(),
    isActive: z.boolean(),
    approvalStatus: z.enum(APPROVAL_STATUSES),
    lastLoginAt: z.string().nullable(),
});
export const sessionResponseSchema = z.object({
    user: userSummarySchema,
    csrfToken: z.string(),
});
export const userListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
    role: z.enum(ROLES).optional(),
    /**
     * Status persetujuan yang ditampilkan. Bawaannya hanya akun APPROVED supaya
     * pemilih peserta kegiatan tidak pernah menampilkan akun yang belum disetujui.
     */
    status: z.enum(["ALL", "PENDING", "APPROVED", "REJECTED"]).default("APPROVED"),
    q: z.string().trim().max(120).optional(),
});
//# sourceMappingURL=auth.js.map